import { getAboveTheLineEstimate, getBelowTheLineEstimate } from "./BudgetItemUtils";
import { BudgetCategory } from "./types";
import { getStructuredTaxIncentiveProgram } from "./TaxIncentives/taxIncentiveStructuredCatalog";
import { StructuredTaxIncentiveProgram } from "./TaxIncentives/taxIncentiveCatalog.types";
import {
  DEFAULT_TAX_INCENTIVE_ASSUMPTIONS,
  QualifiedSpendBreakdown,
  TaxEffectiveRateSummary,
  TaxFrictionLine,
  TaxIncentiveAssumptions,
  TaxNettingSummary,
  JurisdictionCompareAvailability,
} from "./taxIncentiveEffectiveRate.types";

export function computeQualifiedSpendBreakdown(
  categories: BudgetCategory[],
  program: StructuredTaxIncentiveProgram
): QualifiedSpendBreakdown {
  const atlSpend = getAboveTheLineEstimate(categories);
  const btlSpend = getBelowTheLineEstimate(categories);
  const qualifiedAtlSpend = program.qualifiedSpend.atlEligible ? atlSpend : 0;
  const qualifiedBtlSpend = program.qualifiedSpend.btlEligible ? btlSpend : 0;

  return {
    atlSpend,
    btlSpend,
    qualifiedAtlSpend,
    qualifiedBtlSpend,
    qualifiedSpend: qualifiedAtlSpend + qualifiedBtlSpend,
  };
}

function resolveHeadlineRatePercent(
  program: StructuredTaxIncentiveProgram,
  assumptions: TaxIncentiveAssumptions
): number {
  let rate = program.baseRate;

  if (assumptions.applyPromotionalUplift) {
    const promoUplift = program.uplifts.find((uplift) =>
      uplift.label.toLowerCase().includes("promotional")
    );
    if (promoUplift) {
      rate += promoUplift.rate;
    }
  }

  if (assumptions.applyLocationUplift) {
    const locationUplift = program.uplifts.find(
      (uplift) =>
        uplift.label.toLowerCase().includes("location") ||
        uplift.label.toLowerCase().includes("neighbor")
    );
    if (locationUplift) {
      rate += locationUplift.rate;
    }
  }

  const residentUpliftRate = program.qualifiedSpend.residentLaborUpliftRate;
  if (residentUpliftRate && assumptions.residentCrewPercent > 0) {
    rate += (residentUpliftRate * assumptions.residentCrewPercent) / 100;
  }

  return Math.min(rate, program.maxHeadlineRate || rate);
}

function applyCaps(
  headlineCredit: number,
  qualifiedSpend: QualifiedSpendBreakdown,
  program: StructuredTaxIncentiveProgram
): number {
  let cappedCredit = headlineCredit;

  if (program.caps.project) {
    cappedCredit = Math.min(cappedCredit, (program.caps.project * program.maxHeadlineRate) / 100);
  }

  if (program.caps.perLine?.atlLabor && qualifiedSpend.qualifiedAtlSpend > 0) {
    const atlCapCredit = (program.caps.perLine.atlLabor * program.maxHeadlineRate) / 100;
    const atlCredit = (qualifiedSpend.qualifiedAtlSpend * program.maxHeadlineRate) / 100;
    cappedCredit -= Math.max(0, atlCredit - atlCapCredit);
  }

  if (program.caps.perLine?.btlLabor && qualifiedSpend.qualifiedBtlSpend > 0) {
    const btlCapCredit = (program.caps.perLine.btlLabor * program.maxHeadlineRate) / 100;
    const btlCredit = (qualifiedSpend.qualifiedBtlSpend * program.maxHeadlineRate) / 100;
    cappedCredit -= Math.max(0, btlCredit - btlCapCredit);
  }

  return Math.max(cappedCredit, 0);
}

function computeFrictionBreakdown(
  headlineCredit: number,
  qualifiedSpend: number,
  program: StructuredTaxIncentiveProgram,
  assumptions: TaxIncentiveAssumptions
): TaxFrictionLine[] {
  const friction: TaxFrictionLine[] = [];
  const { friction: defaults } = program;

  if (
    program.incentiveType === "transferable" &&
    defaults.brokerDiscountMin != null &&
    defaults.brokerDiscountMax != null
  ) {
    const discountRate = (defaults.brokerDiscountMin + defaults.brokerDiscountMax) / 2 / 100;
    friction.push({
      label: "Transferable credit broker discount",
      amount: headlineCredit * discountRate,
    });
  }

  if (
    defaults.auditRequiredAboveSpend != null &&
    qualifiedSpend >= defaults.auditRequiredAboveSpend &&
    defaults.defaultAuditFee
  ) {
    friction.push({
      label: "CPA audit (estimated)",
      amount: defaults.defaultAuditFee,
    });
  }

  if (defaults.adminFeePercent) {
    const adminFee = Math.min(
      (qualifiedSpend * defaults.adminFeePercent) / 100,
      defaults.adminFeeMax ?? Number.POSITIVE_INFINITY
    );
    if (adminFee > 0) {
      friction.push({
        label: "Program admin / application fees",
        amount: adminFee,
      });
    }
  }

  const nonResidentPenaltyRate = program.qualifiedSpend.nonResidentLaborPenaltyRate;
  if (nonResidentPenaltyRate && assumptions.residentCrewPercent < 100) {
    const nonResidentShare = (100 - assumptions.residentCrewPercent) / 100;
    friction.push({
      label: "Out-of-state labor penalty (estimated)",
      amount: (qualifiedSpend * nonResidentShare * nonResidentPenaltyRate) / 100,
    });
  }

  if (assumptions.usesFinancing && headlineCredit > 0) {
    const annualRate = assumptions.bridgeLoanRatePercent / 100;
    const monthlyRate = annualRate / 12;
    const interest = headlineCredit * monthlyRate * assumptions.financingMonths * 0.5;
    friction.push({
      label: "Bridge loan interest (estimated)",
      amount: interest,
    });
  }

  return friction;
}

function describeQualifiedSpendCategories(program: StructuredTaxIncentiveProgram): string {
  const eligibleParts: string[] = [];
  if (program.qualifiedSpend.atlEligible) {
    eligibleParts.push("above-the-line");
  }
  if (program.qualifiedSpend.btlEligible) {
    eligibleParts.push("below-the-line");
  }
  if (eligibleParts.length === 0) {
    return "any budget categories";
  }
  return eligibleParts.join(" and ");
}

export function resolveJurisdictionCompareAvailability(
  grossBudget: number,
  categories: BudgetCategory[],
  jurisdictionName: string
): JurisdictionCompareAvailability {
  const program = getStructuredTaxIncentiveProgram(jurisdictionName);
  if (!program) {
    return {
      jurisdictionName,
      isAvailable: false,
      unavailableReason: "Program data is not available for this jurisdiction.",
    };
  }

  if (!program.isActive) {
    return {
      jurisdictionName,
      isAvailable: false,
      unavailableReason: "This state does not currently have an active film incentive program.",
    };
  }

  if (grossBudget <= 0) {
    return {
      jurisdictionName,
      isAvailable: false,
      unavailableReason: "Add budget line items before comparing jurisdictions.",
    };
  }

  const spend = computeQualifiedSpendBreakdown(categories, program);
  if (spend.qualifiedSpend <= 0) {
    return {
      jurisdictionName,
      isAvailable: false,
      unavailableReason: `This program only credits ${describeQualifiedSpendCategories(program)} spend, and your budget has none in those categories.`,
    };
  }

  return { jurisdictionName, isAvailable: true };
}

export function computeJurisdictionEffectiveRate(
  grossBudget: number,
  categories: BudgetCategory[],
  jurisdictionName: string,
  assumptions: TaxIncentiveAssumptions = DEFAULT_TAX_INCENTIVE_ASSUMPTIONS
): TaxEffectiveRateSummary | null {
  const program = getStructuredTaxIncentiveProgram(jurisdictionName);
  if (!program || !program.isActive || grossBudget <= 0) {
    return null;
  }

  const spend = computeQualifiedSpendBreakdown(categories, program);
  if (spend.qualifiedSpend <= 0) {
    return null;
  }

  const meetsMinimumSpend =
    program.minimumSpend == null || spend.qualifiedSpend >= program.minimumSpend;

  const headlineRatePercent = resolveHeadlineRatePercent(program, assumptions);
  const rawHeadlineCredit = (spend.qualifiedSpend * headlineRatePercent) / 100;
  const headlineCredit = meetsMinimumSpend ? applyCaps(rawHeadlineCredit, spend, program) : 0;

  const frictionBreakdown = meetsMinimumSpend
    ? computeFrictionBreakdown(headlineCredit, spend.qualifiedSpend, program, assumptions)
    : [];

  const totalFriction = frictionBreakdown.reduce((sum, line) => sum + line.amount, 0);
  const effectiveNetCredit = Math.max(headlineCredit - totalFriction, 0);
  const netCost = Math.max(grossBudget - effectiveNetCredit, 0);
  const effectiveRatePercent =
    spend.qualifiedSpend > 0 ? (effectiveNetCredit / spend.qualifiedSpend) * 100 : 0;

  return {
    grossBudget,
    qualifiedSpend: spend.qualifiedSpend,
    headlineCredit,
    totalFriction,
    effectiveNetCredit,
    netCost,
    headlineRatePercent,
    effectiveRatePercent,
    locationNames: [jurisdictionName],
    primaryJurisdiction: jurisdictionName,
    frictionBreakdown,
    meetsMinimumSpend,
    minimumSpend: program.minimumSpend,
  };
}

export function toTaxNettingSummary(summary: TaxEffectiveRateSummary): TaxNettingSummary {
  return {
    ...summary,
    eligibleSpend: summary.qualifiedSpend,
    estimatedRebate: summary.effectiveNetCredit,
    rebatePercent: Number(summary.effectiveRatePercent.toFixed(1)),
  };
}

export function computeBestJurisdictionEffectiveRate(
  grossBudget: number,
  categories: BudgetCategory[],
  jurisdictionNames: string[],
  assumptions: TaxIncentiveAssumptions = DEFAULT_TAX_INCENTIVE_ASSUMPTIONS
): TaxEffectiveRateSummary | null {
  const results = jurisdictionNames
    .map((name) => computeJurisdictionEffectiveRate(grossBudget, categories, name, assumptions))
    .filter((result): result is TaxEffectiveRateSummary => result != null);

  if (results.length === 0) return null;

  return results.reduce((best, current) =>
    current.effectiveNetCredit > best.effectiveNetCredit ? current : best
  );
}

export function rankJurisdictionEffectiveRates(
  grossBudget: number,
  categories: BudgetCategory[],
  jurisdictionNames: string[],
  assumptions: TaxIncentiveAssumptions = DEFAULT_TAX_INCENTIVE_ASSUMPTIONS
) {
  return jurisdictionNames
    .map((jurisdictionName) => {
      const effectiveRate = computeJurisdictionEffectiveRate(
        grossBudget,
        categories,
        jurisdictionName,
        assumptions
      );
      if (!effectiveRate) return null;
      return {
        jurisdictionName,
        ...effectiveRate,
        rankScore: effectiveRate.effectiveNetCredit,
      };
    })
    .filter((result): result is NonNullable<typeof result> => result != null)
    .sort((a, b) => b.rankScore - a.rankScore);
}
